import './Nav.css'
import { createContext, useEffect, useState } from 'react'
import { Define, NotificationItem } from '../types'
import LOGO from '../images/logo.png'
import StockNavIcon from '../icons/StockNavIcon'
import AttenduNavIcon from '../icons/AttenduNavIcon'
import CommandeNavIcon from '../icons/CommandeNavIcon'
import HistoryNavIcon from '../icons/HistoryNavIcon'
import { checkActive, getLocalUserId } from '../func'
import usePermissions from '../hook/usePermissions'
import NotiBell from '../components/NotiBell'
import { UserRole } from '../types/userRole'
import { initReactI18next } from 'react-i18next'
import i18n, { t } from 'i18next'
import * as en from '../lang/en.json'
import * as fr from '../lang/fr.json'
import LogoutIcon from '../icons/LogoutIcon'
import SettingIcon from '../icons/SettingIcon'
import UserIcon from '../icons/UserIcon'
import MissionsNavIcon from '../icons/MissionNavIcon'
import Tooltip from '../components/tooltip/Tooltip'
import packageInfo from '../../package.json'
import { useUpdateVersion } from '../hook/useUpdateVersion'
import Link from './Link'
import iconProfile from '../icons/user_profile.svg'

/**
 * The @NavBar is a component display the navbar of the web. It includes Stock, Inventory, Configuration, Factory.
 * @author [anhnq]
 * @version 0.1
 * @since 16-Mar
 */

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    react: {
      useSuspense: false,
    },
    // the translations
    resources: {
      en: {
        translation: en,
      },
      fr: {
        translation: fr,
      },
    },
    lng: 'fr', // if you're using a language detector, do not define the lng option
    fallbackLng: 'fr',

    interpolation: {
      escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
  })

export const NavProvider = createContext<{
  permission: any
  account: any
  navigate: (href: string) => void
  actionPage: string
  notificationList: NotificationItem[]
  onNotiClick: any
}>({
  permission: {},
  account: {},
  navigate: () => {},
  actionPage: '',
  notificationList: [],
  onNotiClick: () => {},
})

export function NavBar(props: any) {
  const { permission, account, notificationList, onNotiClick } = props
  useUpdateVersion()

  const username = localStorage.getItem(Define.USERNAME)
  if (!permission || !account) {
    return <div className="nav-bar" />
  }
  const { version } = packageInfo

  const { active, isWMSPermission } = permission || {}
  const [actionPage, setActionPage] = useState('')
  useEffect(() => {
    // listen to url change
    let previousUrl = actionPage

    const checkInterval = setInterval(() => {
      const currentUrl = window.location.pathname
      if (currentUrl !== previousUrl) {
        setActionPage(currentUrl)
      }
    }, 500) // Check every second

    return () => {
      clearInterval(checkInterval)
    }
  }, [actionPage])

  const {
    canAccessStock,
    canAccessAttendu,
    canAccessCommande,
    canAccessMission,
    canAccessHistory,
    canAccessClient,
    canAccessUser,
    canAccessReference,
    canAccessCompany,
    canAccessWarehouse,
    canAccessBilling,
    canAccessInventory,
    canAccessSupport,
    canAccessPrinter,
    canAccessPrintingPolicy,
    canAccessDatabaseTable,
    canAccessFTP,
  } = usePermissions(permission?.permissionList || {})

  const canAccessTiers = true

  function navigate(href) {
    props?.navigate ? props?.navigate(href) : (window.location.href = href)
  }

  const menuStock = [
    canAccessStock && {
      url: '/gestion-de-stock/stock',
      icon: <StockNavIcon />,
      name: t('stock'),
    },
    canAccessAttendu && {
      url: '/gestion-de-stock/attendu',
      icon: <AttenduNavIcon />,
      name: t('attendus'),
    },
    canAccessCommande && {
      url: '/gestion-de-stock/commande',
      icon: <CommandeNavIcon />,
      name: t('commandes'),
    },
    canAccessMission && {
      url: '/gestion-de-stock/mission',
      icon: <MissionsNavIcon />,
      name: t('missions'),
    },
    canAccessHistory && {
      url: '/gestion-de-stock/history',
      icon: <HistoryNavIcon />,
      name: t('histories'),
    },
  ]

  const menuBilling = [
    canAccessBilling && {
      url: '/facturation/facturations',
      name: t('billings'),
    },
    canAccessBilling && {
      url: '/facturation/parametrage',
      name: t('settings'),
    },
  ]

  const menuConfiguration = [
    canAccessClient && {
      url: '/configurations/client',
      name: t('clients'),
    },
    canAccessCompany && {
      url: '/configurations/societe',
      name: t('companies'),
    },
    canAccessUser && {
      url: '/configurations/utilisateur',
      name: t('users'),
    },
    canAccessReference && {
      url: '/configurations/reference',
      name: t('references'),
    },
    canAccessWarehouse && {
      url: '/wh/configuration',
      name: t('warehouses'),
    },

    canAccessTiers && {
      url: '/configurations/tiers',
      name: t('tiers'),
    },
    canAccessSupport && {
      url: '/configurations/materials',
      name: t('supports'),
    },
  ]

  const menuSettings = []
  if (canAccessPrinter || canAccessPrintingPolicy) {
    menuSettings.push({
      url: '/printers',
      name: t('imprimers'),
    })
  }
  if (canAccessDatabaseTable) {
    menuSettings.push({
      url: '/tables',
      name: t('tables'),
    })
  }
  if (canAccessFTP) {
    menuSettings.push({
      url: '/server-config',
      name: t('server'),
      noList: true,
    })
  }

  const USERNAME_MAX_DIGITS = 20

  function logout() {
    localStorage.removeItem(Define.ACCESS_TOKEN)
    localStorage.removeItem(Define.REFRESH_TOKEN)
    localStorage.removeItem(Define.ACCESS_TOKEN_EXP_AT)
    localStorage.removeItem(Define.REFRESH_TOKEN_EXP_AT)
    localStorage.removeItem(Define.CLIENT_ID)
    localStorage.removeItem(Define.ROLE)
    localStorage.removeItem(Define.USERNAME)
    localStorage.removeItem(Define.VIEWS)
    window.location.href = '/signin'
  }

  const isAccountValid = active && isWMSPermission
  const isNavItemValid = (condition: boolean) => {
    if (!isAccountValid) {
      return false
    }
    return permission.role === UserRole.SUPERADMIN || condition
  }

  const canShowGestionDeStock = isNavItemValid(menuStock.length > 0)
  const canShowConfigurations = isNavItemValid(menuConfiguration.length > 0)
  const canShowBilling = isNavItemValid(canAccessBilling)
  const canShowInventory = isNavItemValid(canAccessInventory)

  return (
    <NavProvider.Provider
      value={{
        permission,
        account,
        navigate,
        actionPage,
        notificationList,
        onNotiClick,
      }}
    >
      <nav className="nav-bar">
        <div className="left-nav-bar">
          <div className="icon">
            <img alt="logo" src={LOGO} />
          </div>
          {isAccountValid && (
            <Link
              id="tableaudebord_menu"
              item={{ url: '/tableau-de-bord', name: t('dashboard') }}
              index={0}
              checkActive={checkActive}
              navigate={navigate}
            />
          )}
          {canShowGestionDeStock && (
            <div className="dropdown-wrapper">
              <div className="bf-link">Gestion de Stock</div>
              <div className="nav-dropdown">
                {menuStock.map((item, index) =>
                  item ? (
                    <Link
                      key={index}
                      item={item}
                      index={index}
                      checkActive={checkActive}
                      navigate={navigate}
                    />
                  ) : null
                )}
              </div>
            </div>
          )}
          {canShowBilling && (
            <div className="dropdown-wrapper">
              <div className="bf-link">Facturation</div>
              <div className="nav-dropdown">
                {menuBilling.map((item, index) => (
                  <Link
                    key={index}
                    item={item}
                    index={index}
                    checkActive={checkActive}
                    navigate={navigate}
                  />
                ))}
              </div>
            </div>
          )}
          {canShowInventory && (
            <div className="dropdown-wrapper">
              <Link
                item={{ url: '/inventories', name: t('inventory') }}
                index={0}
                checkActive={checkActive}
                navigate={navigate}
              />
            </div>
          )}
          {canShowConfigurations && (
            <div className="dropdown-wrapper">
              <div className="bf-link">Configurations</div>
              <div className="nav-dropdown">
                {menuConfiguration.map((item, index) =>
                  item ? (
                    <Link
                      key={index}
                      item={item}
                      index={index}
                      checkActive={checkActive}
                      navigate={navigate}
                    />
                  ) : null
                )}
              </div>
            </div>
          )}
          <div className="dropdown-wrapper">
            <div className="bf-link">{t('parameters')}</div>
            <div className="nav-dropdown">
              {menuSettings.map((item, index) => (
                <Link
                  key={index}
                  item={item}
                  index={index}
                  checkActive={checkActive}
                  navigate={navigate}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="profile">
          <NotiBell />
          <div>
            {username ? (
              <div className="dropdown-wrapper">
                {username?.length > USERNAME_MAX_DIGITS ? (
                  <Tooltip message={username}>
                    <div className="inline-center">
                      <UserIcon />
                      <div className="user-name">
                        {username.substring(0, USERNAME_MAX_DIGITS)}
                      </div>
                    </div>
                  </Tooltip>
                ) : (
                  <div className="inline-center">
                    <UserIcon />
                    {username}
                  </div>
                )}
                <div
                  className="nav-dropdown"
                  style={{ right: 10, backgroundColor: 'white', width: 200 }}
                >
                  <div
                    className="item gray"
                    onClick={() => {
                      const userId = getLocalUserId()
                      navigate(
                        `/configurations/utilisateur/${userId}?page-index=1&page-size=25`
                      )
                    }}
                  >
                    <img src={iconProfile} alt="my_profile" />
                    {t('myProfile')}
                  </div>
                  <div className="item gray">
                    <SettingIcon />
                    {t('parameters')}
                  </div>
                  <div
                    className="item gray"
                    onClick={logout}
                    style={{ cursor: 'pointer' }}
                  >
                    <LogoutIcon />
                    {t('logout')}
                  </div>
                  <p className="break-all w-full my-4 !text-black">
                    {version}
                    {process.env.REACT_APP_BUILD_VERSION
                      ? ` - ${process.env.REACT_APP_BUILD_VERSION}`
                      : ''}
                  </p>
                </div>
              </div>
            ) : (
              <div />
            )}
          </div>
        </div>
      </nav>
    </NavProvider.Provider>
  )
}
